import { useState, type ChangeEvent, type KeyboardEvent } from 'react';

export interface DraftFieldProps {
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

/**
 * Text field that saves when it is left (or with Enter in a single-line field), not with every
 * key: fast switching between fields loses nothing, and a list of fields does not write once per
 * letter. Shows the typed text until then, the value from outside otherwise.
 */
export function useDraft(value: string, onCommit: (value: string) => void): DraftFieldProps {
  const [draft, setDraft] = useState<string | null>(null);

  function commit() {
    if (draft !== null && draft !== value) onCommit(draft);
    setDraft(null);
  }

  return {
    value: draft ?? value,
    onChange: (event) => setDraft(event.target.value),
    onBlur: commit,
    onKeyDown: (event) => {
      if (event.key === 'Enter' && event.currentTarget instanceof HTMLInputElement) commit();
    },
  };
}
